import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  Plane,
  Edit2,
  Trash2,
  User,
  ShoppingCart,
  Loader2,
  Save,
  X,
  Send,
  FileText,
  Download,
  ChevronDown,
  MessageSquare,
  Receipt,
  FileSignature,
} from "lucide-react";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import { useState } from "react";
import toast from "react-hot-toast";
import clsx from "clsx";

interface Template {
  id: string;
  name: string;
  type: "email" | "pdf";
  category: string;
  subject?: string;
}

interface Communication {
  id: string;
  type: "email" | "phone" | "note" | "sms";
  direction: "inbound" | "outbound";
  subject?: string;
  content?: string;
  emailStatus?: string;
  createdAt: string;
  createdBy?: string;
}

interface Order {
  id: string;
  orderNumber: string;
  status: string;
  totalPrice: number;
  createdAt: string;
  trip?: {
    title: string;
  };
  tripDate?: {
    startDate: string;
  };
}

interface Customer {
  id: string;
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  city?: string;
  zip?: string;
  country?: string;
  birthDate?: string;
  passportNumber?: string;
  passportExpiry?: string;
  nationality?: string;
  totalSpent: number;
  tripsCount: number;
  firstTripDate?: string;
  lastTripDate?: string;
  score: number;
  newsletterSubscribed: boolean;
  notes?: string;
  source?: string;
  createdAt: string;
  orders?: Order[];
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  inquiry: { label: "Poptávka", className: "bg-gray-100 text-gray-700" },
  quoted: { label: "Naceneno", className: "bg-blue-100 text-blue-700" },
  confirmed: { label: "Potvrzeno", className: "bg-purple-100 text-purple-700" },
  deposit_paid: { label: "Záloha", className: "bg-warning-100 text-warning-700" },
  fully_paid: { label: "Zaplaceno", className: "bg-success-100 text-success-700" },
  completed: { label: "Dokončeno", className: "bg-success-100 text-success-700" },
  cancelled: { label: "Zrušeno", className: "bg-error-100 text-error-700" },
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Customer>>({});
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<"email" | "pdf">("email");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["customer", id],
    queryFn: () => api.get<ApiResponse<Customer>>(`/api/admin/customers/${id}`),
    enabled: !!id,
  });

  // Fetch templates for sending
  const { data: templatesData } = useQuery({
    queryKey: ["templates-for-sending"],
    queryFn: () => api.get<ApiResponse<Template[]>>("/api/admin/templates?is_active=true"),
  });

  // Fetch communications
  const { data: communicationsData } = useQuery({
    queryKey: ["customer-communications", id],
    queryFn: () => api.get<ApiResponse<Communication[]>>(`/api/admin/customers/${id}/communications`),
    enabled: !!id,
  });

  const templates = templatesData?.data || [];
  const communications = communicationsData?.data || [];

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Customer>) =>
      api.put<ApiResponse<Customer>>(`/api/admin/customers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer", id] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/admin/customers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      navigate("/customers");
    },
  });

  // Send email/document mutation
  const sendMutation = useMutation({
    mutationFn: (data: { templateId: string; orderId?: string }) =>
      api.post(`/api/admin/customers/${id}/send`, data),
    onSuccess: () => {
      toast.success("Zpráva odeslána");
      queryClient.invalidateQueries({ queryKey: ["customer-communications", id] });
      setShowSendModal(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se odeslat");
    },
  });

  const customer = data?.data;

  const handleOpenSendModal = (type: "email" | "pdf", order?: Order) => {
    setSelectedTemplateType(type);
    setSelectedOrder(order || null);
    setShowSendModal(true);
    setShowActionsMenu(false);
  };

  const handleDownloadDocument = async (docType: "contract" | "invoice", order: Order) => {
    try {
      toast.loading(`Generuji ${docType === "contract" ? "smlouvu" : "fakturu"}...`, { id: "download" });

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/admin/orders/${order.id}/document/${docType}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Nepodařilo se vygenerovat dokument");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docType === "contract" ? "smlouva" : "faktura"}_${order.orderNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`${docType === "contract" ? "Smlouva" : "Faktura"} stažena`, { id: "download" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Chyba při stahování", { id: "download" });
    }
  };

  const handleEdit = () => {
    if (customer) {
      setEditData({
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: customer.phone,
        street: customer.street,
        city: customer.city,
        zip: customer.zip,
        country: customer.country,
        birthDate: customer.birthDate,
        passportNumber: customer.passportNumber,
        passportExpiry: customer.passportExpiry,
        nationality: customer.nationality,
        notes: customer.notes,
        newsletterSubscribed: customer.newsletterSubscribed,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editData);
  };

  const handleDelete = () => {
    if (confirm("Opravdu chcete smazat tohoto zákazníka? Tato akce je nevratná.")) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="card text-center text-error-600">
        Zákazník nenalezen nebo došlo k chybě.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/customers")} className="btn-ghost btn-sm">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-xl font-bold text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
              {customer.firstName?.[0]}
              {customer.lastName?.[0]}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {customer.firstName} {customer.lastName}
              </h1>
              <p className="text-gray-500">{customer.email}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button onClick={() => setIsEditing(false)} className="btn-ghost">
                <X className="mr-2 h-4 w-4" />
                Zrušit
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="btn-primary"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Uložit
              </button>
            </>
          ) : (
            <>
              {/* Quick Actions Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="btn-primary"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Odeslat
                  <ChevronDown className="ml-2 h-4 w-4" />
                </button>

                {showActionsMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowActionsMenu(false)}
                    />
                    <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                      <div className="border-b border-gray-200 px-3 py-2 dark:border-gray-700">
                        <p className="text-xs font-medium uppercase text-gray-500">
                          Emaily
                        </p>
                      </div>
                      <button
                        onClick={() => handleOpenSendModal("email")}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <Mail className="h-4 w-4" />
                        Poslat email
                      </button>
                      <button
                        onClick={() => handleOpenSendModal("email")}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <Receipt className="h-4 w-4" />
                        Poslat potvrzení
                      </button>

                      <div className="border-b border-t border-gray-200 px-3 py-2 dark:border-gray-700">
                        <p className="text-xs font-medium uppercase text-gray-500">
                          Dokumenty
                        </p>
                      </div>
                      <button
                        onClick={() => handleOpenSendModal("pdf")}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <FileSignature className="h-4 w-4" />
                        Poslat smlouvu
                      </button>
                      <button
                        onClick={() => handleOpenSendModal("pdf")}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <FileText className="h-4 w-4" />
                        Poslat fakturu
                      </button>
                    </div>
                  </>
                )}
              </div>

              <button onClick={handleEdit} className="btn-ghost">
                <Edit2 className="mr-2 h-4 w-4" />
                Upravit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="btn-ghost text-error-600 hover:bg-error-50"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Smazat
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <CreditCard className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Celkem utraceno</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {formatPrice(customer.totalSpent || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-100 dark:bg-success-900/30">
              <Plane className="h-5 w-5 text-success-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Počet zájezdů</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {customer.tripsCount || 0}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning-100 dark:bg-warning-900/30">
              <ShoppingCart className="h-5 w-5 text-warning-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Objednávek</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {customer.orders?.length || 0}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <User className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Skóre</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {customer.score || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Customer info */}
        <div className="lg:col-span-1">
          <div className="card space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Informace o zákazníkovi
            </h2>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Jméno
                    </label>
                    <input
                      type="text"
                      value={editData.firstName || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, firstName: e.target.value })
                      }
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Příjmení
                    </label>
                    <input
                      type="text"
                      value={editData.lastName || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, lastName: e.target.value })
                      }
                      className="input"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editData.email || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, email: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={editData.phone || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, phone: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Ulice
                  </label>
                  <input
                    type="text"
                    value={editData.street || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, street: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Město
                    </label>
                    <input
                      type="text"
                      value={editData.city || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, city: e.target.value })
                      }
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      PSČ
                    </label>
                    <input
                      type="text"
                      value={editData.zip || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, zip: e.target.value })
                      }
                      className="input"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Datum narození
                  </label>
                  <input
                    type="date"
                    value={editData.birthDate || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, birthDate: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Číslo pasu
                  </label>
                  <input
                    type="text"
                    value={editData.passportNumber || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, passportNumber: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Platnost pasu
                  </label>
                  <input
                    type="date"
                    value={editData.passportExpiry || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, passportExpiry: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Poznámky
                  </label>
                  <textarea
                    rows={3}
                    value={editData.notes || ""}
                    onChange={(e) =>
                      setEditData({ ...editData, notes: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editData.newsletterSubscribed || false}
                    onChange={(e) =>
                      setEditData({ ...editData, newsletterSubscribed: e.target.checked })
                    }
                    className="h-4 w-4 rounded text-primary-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Odběr newsletteru
                  </span>
                </label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                  <Mail className="h-5 w-5" />
                  <span>{customer.email}</span>
                </div>
                {customer.phone && (
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                    <Phone className="h-5 w-5" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {(customer.street || customer.city) && (
                  <div className="flex items-start gap-3 text-gray-600 dark:text-gray-400">
                    <MapPin className="h-5 w-5 shrink-0" />
                    <div>
                      {customer.street && <p>{customer.street}</p>}
                      <p>
                        {customer.zip} {customer.city}
                        {customer.country && `, ${customer.country}`}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                  <Calendar className="h-5 w-5" />
                  <span>Registrován: {formatDate(customer.createdAt)}</span>
                </div>

                {customer.birthDate && (
                  <div className="border-t pt-4 dark:border-gray-700">
                    <p className="text-sm text-gray-500">Datum narození</p>
                    <p className="text-gray-900 dark:text-white">
                      {formatDate(customer.birthDate)}
                    </p>
                  </div>
                )}

                {customer.passportNumber && (
                  <div>
                    <p className="text-sm text-gray-500">Číslo pasu</p>
                    <p className="text-gray-900 dark:text-white">
                      {customer.passportNumber}
                      {customer.passportExpiry && (
                        <span className="ml-2 text-sm text-gray-500">
                          (platný do {formatDate(customer.passportExpiry)})
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {customer.notes && (
                  <div className="border-t pt-4 dark:border-gray-700">
                    <p className="text-sm text-gray-500">Poznámky</p>
                    <p className="whitespace-pre-wrap text-gray-900 dark:text-white">
                      {customer.notes}
                    </p>
                  </div>
                )}

                <div className="border-t pt-4 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    {customer.newsletterSubscribed ? (
                      <span className="badge bg-success-100 text-success-700">
                        Newsletter aktivní
                      </span>
                    ) : (
                      <span className="badge bg-gray-100 text-gray-600">
                        Newsletter neaktivní
                      </span>
                    )}
                    {customer.source && (
                      <span className="badge bg-blue-100 text-blue-700">
                        Zdroj: {customer.source}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Orders history */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Historie objednávek
              </h2>
              <Link to="/orders/new" className="btn-primary btn-sm">
                Nová objednávka
              </Link>
            </div>

            {customer.orders && customer.orders.length > 0 ? (
              <div className="space-y-3">
                {customer.orders.map((order) => {
                  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.inquiry;
                  return (
                    <div
                      key={order.id}
                      className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                    >
                      <div className="flex items-center justify-between">
                        <Link
                          to={`/orders/${order.id}`}
                          className="flex-1 transition-colors hover:text-primary-600"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {order.orderNumber}
                            </span>
                            <span className={`badge text-xs ${statusConfig.className}`}>
                              {statusConfig.label}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {order.trip?.title || "Neznámý zájezd"}
                            {order.tripDate && (
                              <span className="ml-2">
                                ({formatDate(order.tripDate.startDate)})
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-400">
                            Vytvořeno: {formatDate(order.createdAt)}
                          </p>
                        </Link>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {formatPrice(order.totalPrice || 0)}
                          </p>
                        </div>
                      </div>

                      {/* Document download buttons */}
                      <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
                        <button
                          onClick={() => handleDownloadDocument("contract", order)}
                          className="btn-ghost btn-sm text-xs"
                          title="Stáhnout smlouvu"
                        >
                          <FileSignature className="mr-1.5 h-4 w-4" />
                          Smlouva
                        </button>
                        <button
                          onClick={() => handleDownloadDocument("invoice", order)}
                          className="btn-ghost btn-sm text-xs"
                          title="Stáhnout fakturu"
                        >
                          <Receipt className="mr-1.5 h-4 w-4" />
                          Faktura
                        </button>
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            handleOpenSendModal("email");
                          }}
                          className="btn-ghost btn-sm text-xs"
                          title="Odeslat email"
                        >
                          <Send className="mr-1.5 h-4 w-4" />
                          Email
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex h-32 flex-col items-center justify-center text-gray-500">
                <ShoppingCart className="mb-2 h-8 w-8 text-gray-300" />
                <p>Žádné objednávky</p>
              </div>
            )}

            {/* Timeline */}
            {customer.firstTripDate && (
              <div className="mt-6 border-t pt-6 dark:border-gray-700">
                <h3 className="mb-4 font-medium text-gray-900 dark:text-white">
                  Časová osa
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-success-500" />
                    <span className="text-gray-600 dark:text-gray-400">
                      První zájezd: {formatDate(customer.firstTripDate)}
                    </span>
                  </div>
                  {customer.lastTripDate && customer.lastTripDate !== customer.firstTripDate && (
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-2 rounded-full bg-primary-500" />
                      <span className="text-gray-600 dark:text-gray-400">
                        Poslední zájezd: {formatDate(customer.lastTripDate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Communications section */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Komunikace
          </h2>
          <button
            onClick={() => handleOpenSendModal("email")}
            className="btn-ghost btn-sm"
          >
            <Send className="mr-2 h-4 w-4" />
            Nová zpráva
          </button>
        </div>

        {communications.length > 0 ? (
          <div className="space-y-3">
            {communications.map((comm) => {
              const typeConfig: Record<string, { icon: typeof Mail; color: string; label: string }> = {
                email: { icon: Mail, color: "text-blue-600", label: "Email" },
                phone: { icon: Phone, color: "text-green-600", label: "Telefon" },
                sms: { icon: MessageSquare, color: "text-purple-600", label: "SMS" },
                note: { icon: FileText, color: "text-gray-600", label: "Poznámka" },
              };
              const config = typeConfig[comm.type] || typeConfig.note;
              const Icon = config.icon;

              return (
                <div
                  key={comm.id}
                  className="flex gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                >
                  <div className={clsx("mt-1", config.color)}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {comm.subject || config.label}
                        </span>
                        <span
                          className={clsx(
                            "rounded px-2 py-0.5 text-xs",
                            comm.direction === "outbound"
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          )}
                        >
                          {comm.direction === "outbound" ? "Odesláno" : "Přijato"}
                        </span>
                        {comm.emailStatus && (
                          <span
                            className={clsx(
                              "rounded px-2 py-0.5 text-xs",
                              comm.emailStatus === "delivered"
                                ? "bg-success-100 text-success-700"
                                : comm.emailStatus === "opened"
                                  ? "bg-primary-100 text-primary-700"
                                  : "bg-gray-100 text-gray-600"
                            )}
                          >
                            {comm.emailStatus === "delivered"
                              ? "Doručeno"
                              : comm.emailStatus === "opened"
                                ? "Otevřeno"
                                : comm.emailStatus}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(comm.createdAt)}
                      </span>
                    </div>
                    {comm.content && (
                      <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                        {comm.content}
                      </p>
                    )}
                    {comm.createdBy && (
                      <p className="mt-1 text-xs text-gray-500">
                        Od: {comm.createdBy}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex h-32 flex-col items-center justify-center text-gray-500">
            <MessageSquare className="mb-2 h-8 w-8 text-gray-300" />
            <p>Žádná komunikace</p>
          </div>
        )}
      </div>

      {/* Send Email/Document Modal */}
      {showSendModal && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setShowSendModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedTemplateType === "email" ? "Odeslat email" : "Odeslat dokument"}
                </h3>
                <button
                  onClick={() => setShowSendModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Recipient info */}
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Příjemce:
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {customer.firstName} {customer.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{customer.email}</p>
                </div>

                {/* Order selection (if applicable) */}
                {customer.orders && customer.orders.length > 0 && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Objednávka (volitelné)
                    </label>
                    <select
                      value={selectedOrder?.id || ""}
                      onChange={(e) => {
                        const order = customer.orders?.find(
                          (o) => o.id === e.target.value
                        );
                        setSelectedOrder(order || null);
                      }}
                      className="input"
                    >
                      <option value="">Bez objednávky</option>
                      {customer.orders.map((order) => (
                        <option key={order.id} value={order.id}>
                          {order.orderNumber} - {order.trip?.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Template selection */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Šablona
                  </label>
                  <select id="template-select" className="input">
                    <option value="">Vyberte šablonu</option>
                    {templates
                      .filter((t) => t.type === selectedTemplateType)
                      .map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Preview hint */}
                <p className="text-xs text-gray-500">
                  Proměnné jako jméno zákazníka, číslo objednávky atd. budou
                  automaticky nahrazeny.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowSendModal(false)}
                  className="btn-ghost"
                >
                  Zrušit
                </button>
                <button
                  onClick={() => {
                    const templateSelect = document.getElementById(
                      "template-select"
                    ) as HTMLSelectElement;
                    const templateId = templateSelect?.value;
                    if (!templateId) {
                      toast.error("Vyberte šablonu");
                      return;
                    }
                    sendMutation.mutate({
                      templateId,
                      orderId: selectedOrder?.id,
                    });
                  }}
                  disabled={sendMutation.isPending}
                  className="btn-primary"
                >
                  {sendMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : selectedTemplateType === "email" ? (
                    <Send className="mr-2 h-4 w-4" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {selectedTemplateType === "email" ? "Odeslat" : "Vygenerovat a odeslat"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
